import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useHttpClient } from "../../../../../shared/hooks/http-hook";
import { useLoading } from "../../../../../shared/context/LoadingContext";

import {
  Container,
  Typography,
  Box,
  Paper,
  TextField,
  MenuItem,
  Button,
  CircularProgress,
  Chip,
  IconButton,
  Stack,
  Tooltip,
  Badge,
  Card,
  CardContent,
  Grid,
  FormControlLabel,
  Checkbox,
  Avatar,
} from "@mui/material";
import { DataGrid, GridToolbar, ptBR } from "@mui/x-data-grid";
import RefreshIcon from "@mui/icons-material/Refresh";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import EmailIcon from "@mui/icons-material/Email";
import CallIcon from "@mui/icons-material/Call";
import SmsIcon from "@mui/icons-material/Sms";
import PersonOffIcon from "@mui/icons-material/PersonOff";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import MedicalServicesIcon from "@mui/icons-material/MedicalServices";
import SendIcon from "@mui/icons-material/Send";

// ─── Card de estatísticas por categoria ───────────────────────────────────────
const CategoriaCard = ({ titulo, quantidade, icone, cor, descricao }) => (
  <Card sx={{ height: "100%" }}>
    <CardContent>
      <Stack direction="row" spacing={2} alignItems="center" mb={1}>
        <Avatar sx={{ bgcolor: `${cor}.light`, color: `${cor}.dark` }}>
          {icone}
        </Avatar>
        <Box flex={1}>
          <Typography variant="h6" component="div">
            {titulo}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {descricao}
          </Typography>
        </Box>
      </Stack>
      <Typography variant="h3" color={cor} textAlign="center" mt={2}>
        {quantidade}
      </Typography>
    </CardContent>
  </Card>
);

function RelatorioInatividade({ dadosClinica, auth, acao, atualizacoes }) {
  const { sendRequest } = useHttpClient();
  const { startLoading, stopLoading } = useLoading();

  // ─── Estados ────────────────────────────────────────────────────────────────
  const [clientes, setClientes] = useState([]);
  const [error, setError] = useState(null);
  const [periodo, setPeriodo] = useState(90);
  const [incluirTratamentosIsolados, setIncluirTratamentosIsolados] =
    useState(true);

  // IDs seleccionados na DataGrid (MUI 5.x usa array de ids)
  const [selectionModel, setSelectionModel] = useState([]);

  const [estatisticas, setEstatisticas] = useState({
    total: 0,
    porCategoria: {
      FIEL_INATIVO: 0,
      TRATAMENTO_ABANDONADO: 0,
      ESPORADICO: 0,
    },
  });

  // ─── Opções de período ──────────────────────────────────────────────────────
  const periodosOptions = [
    { value: 30, label: "30 dias" },
    { value: 60, label: "60 dias" },
    { value: 90, label: "90 dias" },
    { value: 120, label: "120 dias" },
    { value: 180, label: "6 meses" },
    { value: 365, label: "1 ano" },
  ];

  // ─── Mapeamento de categorias ───────────────────────────────────────────────
  const categoriasInfo = {
    FIEL_INATIVO: {
      label: "Fiéis Inativos",
      color: "success",
      icone: <CheckCircleIcon />,
      descricao: "Completaram tratamentos",
    },
    TRATAMENTO_ABANDONADO: {
      label: "Tratamentos Abandonados",
      color: "error",
      icone: <CancelIcon />,
      descricao: "Não completaram planos",
    },
    ESPORADICO: {
      label: "Esporádicos",
      color: "info",
      icone: <MedicalServicesIcon />,
      descricao: "Apenas tratamentos isolados",
    },
  };

  // ─── Dispara quando o pai muda acao ────────────────────────────────────────
  useEffect(() => {
    if (acao === "gerar") {
      fetchClientesInativos();
    }
  }, [acao, atualizacoes]);

  // ─── Fetch principal ────────────────────────────────────────────────────────
  const fetchClientesInativos = async () => {
    startLoading();
    setError(null);
    setSelectionModel([]); // limpa seleção ao recarregar
    try {
      const formData = new FormData();
      formData.append("periodo", periodo);
      formData.append("incluirTratamentosIsolados", incluirTratamentosIsolados);

      const response = await sendRequest(
        `${process.env.REACT_APP_BACKEND_LINK}/api/planostratamento/getClientesInativosPorCategoria/${dadosClinica._id}`,
        "PATCH",
        formData,
        { Authorization: "Bearer " + auth.token },
      );

      const clientesComId = response.clientes.map((cliente, index) => ({
        ...cliente,
        id: cliente.clienteId || index,
      }));

      setClientes(clientesComId);
      setEstatisticas(response.estatisticas);
    } catch (err) {
      setError(err.response?.message || "Erro ao buscar dados.");
    } finally {
      stopLoading();
    }
  };

  // ─── Clientes seleccionados (objectos completos, não apenas IDs) ────────────
  // Necessário para ter email, nome, etc. disponíveis no handler de email
  const clientesSelecionados = clientes.filter((c) =>
    selectionModel.includes(c.id),
  );

  // ─── Handler do botão de email — implementação fica do teu lado ────────────
  const handleEnviarEmails = () => {
    // clientesSelecionados já contém os objectos completos com
    // { clienteId, nome, email, contacto, categoria, ... }
    // Passa para o teu sistema de envio de emails aqui
  };

  // ─── Colunas da DataGrid ────────────────────────────────────────────────────
  const columns = [
    {
      field: "categoria",
      headerName: "Categoria",
      width: 220,
      renderCell: (params) => {
        const info = categoriasInfo[params.value];
        if (!info) return null;
        return (
          <Chip
            icon={info.icone}
            label={info.label}
            color={info.color}
            size="small"
            variant="filled"
          />
        );
      },
    },
    {
      field: "nome",
      headerName: "Nome",
      flex: 2,
      minWidth: 200,
      renderCell: (params) => (
        <span className="nomeDataTable">
          <Link to={`/clientes/${params.row.clienteId}-planotratamento`}>
            {params.row.nome}
          </Link>
        </span>
      ),
    },
    {
      field: "contacto",
      headerName: "Contacto",
      flex: 1,
      minWidth: 150,
      renderCell: (params) => {
        const contactoPrincipal = params.row.contacto;
        const contactoSecundario = params.row.contacto2;
        const temContacto = contactoPrincipal || contactoSecundario;
        const prefereSMS = params.row.preferencias?.sms;
        const prefereWhatsApp = params.row.preferencias?.whatsapp;
        const numero = contactoPrincipal || contactoSecundario;

        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {temContacto ? numero : "Não informado"}

            {temContacto && (
              <Stack direction="row" spacing={0.5}>
                <Tooltip title="Ligar">
                  <IconButton size="small" href={`tel:${numero}`}>
                    <CallIcon fontSize="small" />
                  </IconButton>
                </Tooltip>

                <Tooltip
                  title={
                    prefereWhatsApp ? "WhatsApp (preferencial)" : "WhatsApp"
                  }
                >
                  <IconButton
                    size="small"
                    href={`https://wa.me/${numero.replace(/[^0-9]/g, "")}`}
                    target="_blank"
                    color="success"
                  >
                    {prefereWhatsApp ? (
                      <Badge color="primary" variant="dot">
                        <WhatsAppIcon fontSize="small" />
                      </Badge>
                    ) : (
                      <WhatsAppIcon fontSize="small" />
                    )}
                  </IconButton>
                </Tooltip>

                {prefereSMS && (
                  <Tooltip title="SMS (preferencial)">
                    <IconButton
                      size="small"
                      href={`sms:${numero}`}
                      color="primary"
                    >
                      <Badge color="primary" variant="dot">
                        <SmsIcon fontSize="small" />
                      </Badge>
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>
            )}
          </Box>
        );
      },
    },
    {
      field: "email",
      headerName: "Email",
      flex: 1.5,
      minWidth: 200,
      renderCell: (params) => {
        const email = params.value;
        const prefereEmail = params.row.preferencias?.email;

        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {email || "Não informado"}
            {email && (
              <Tooltip
                title={prefereEmail ? "Email (preferencial)" : "Enviar e-mail"}
              >
                <IconButton size="small" href={`mailto:${email}`}>
                  {prefereEmail ? (
                    <Badge color="primary" variant="dot">
                      <EmailIcon fontSize="small" />
                    </Badge>
                  ) : (
                    <EmailIcon fontSize="small" />
                  )}
                </IconButton>
              </Tooltip>
            )}
          </Box>
        );
      },
    },
    {
      field: "ultimaVisita",
      headerName: "Última Visita",
      flex: 1,
      minWidth: 120,
      valueFormatter: (params) => {
        if (!params.value) return "Não registrada";
        return new Date(params.value).toLocaleDateString("pt-BR");
      },
    },
    {
      field: "diasInativo",
      headerName: "Inativo há",
      flex: 1,
      minWidth: 120,
      renderCell: (params) => {
        const dias = params.row.diasInativo || 0;
        let texto = "";
        let color = "default";

        if (dias > 365) {
          const anos = Math.floor(dias / 365);
          texto = `${anos} ${anos === 1 ? "ano" : "anos"}`;
          color = "error";
        } else if (dias > 30) {
          const meses = Math.round(dias / 30);
          texto = `${meses} ${meses === 1 ? "mês" : "meses"}`;
          color = dias > 180 ? "error" : "warning";
        } else {
          texto = `${dias} ${dias === 1 ? "dia" : "dias"}`;
          color = "info";
        }

        return (
          <Chip
            icon={<PersonOffIcon />}
            label={texto}
            color={color}
            size="small"
            variant="filled"
          />
        );
      },
    },
    {
      field: "detalhes",
      headerName: "Detalhes",
      width: 180,
      renderCell: (params) => {
        let detalhes = "";
        switch (params.row.categoria) {
          case "FIEL_INATIVO":
            detalhes = `${params.row.qtdPlanosCompletos || 0} plano(s) completo(s)`;
            break;
          case "TRATAMENTO_ABANDONADO":
            detalhes = `${params.row.qtdPlanosAbandonados || 0} plano(s) abandonado(s)`;
            break;
          case "ESPORADICO":
            detalhes = `${params.row.qtdTratamentos || 0} tratamento(s)`;
            break;
          default:
            detalhes = "-";
        }
        return <Typography variant="body2">{detalhes}</Typography>;
      },
    },
  ];

  return (
    <Container maxWidth="xl">
      <Box sx={{ my: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Clientes Inativos por Categoria
        </Typography>

        {/* Cards de estatísticas */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {Object.entries(categoriasInfo).map(([key, info]) => (
            <Grid item xs={12} sm={6} md={4} key={key}>
              <CategoriaCard
                titulo={info.label}
                quantidade={estatisticas.porCategoria[key] ?? 0}
                icone={info.icone}
                cor={info.color}
                descricao={info.descricao}
              />
            </Grid>
          ))}
        </Grid>

        {/* Filtros */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Stack spacing={3}>
            <Typography variant="h6">Filtros</Typography>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              alignItems="center"
            >
              <TextField
                select
                label="Período de Inatividade"
                value={periodo}
                onChange={(e) => setPeriodo(e.target.value)}
                sx={{ minWidth: 200 }}
              >
                {periodosOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    Há mais de {option.label}
                  </MenuItem>
                ))}
              </TextField>

              <FormControlLabel
                control={
                  <Checkbox
                    checked={incluirTratamentosIsolados}
                    onChange={(e) =>
                      setIncluirTratamentosIsolados(e.target.checked)
                    }
                  />
                }
                label="Incluir tratamentos isolados"
              />

              <Button
                variant="contained"
                onClick={fetchClientesInativos}
                startIcon={<RefreshIcon />}
              >
                Filtrar
              </Button>
            </Stack>
          </Stack>
        </Paper>

        {error && (
          <Paper
            sx={{
              p: 2,
              mb: 3,
              bgcolor: "error.light",
              color: "error.contrastText",
            }}
          >
            <Typography>{error}</Typography>
          </Paper>
        )}

        {/* Barra de acções sobre selecção */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 1,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            {selectionModel.length > 0
              ? `${selectionModel.length} cliente(s) selecionado(s)`
              : `Total: ${estatisticas.total} clientes inativos — período: últimos ${periodo} dias`}
          </Typography>

          {/* Botão de email — só activo quando há selecção com email */}
          {/* <Tooltip
            title={
              selectionModel.length === 0
                ? "Seleciona pelo menos um cliente"
                : `Enviar email a ${clientesSelecionados.filter((c) => c.email).length} cliente(s) com email`
            }
          >
            <span>
              {" "}
              <Button
                variant="contained"
                color="primary"
                startIcon={<SendIcon />}
                onClick={handleEnviarEmails}
                disabled={
                  selectionModel.length === 0 ||
                  clientesSelecionados.filter((c) => c.email).length === 0
                }
              >
                Enviar Email
                {clientesSelecionados.filter((c) => c.email).length > 0 &&
                  ` (${clientesSelecionados.filter((c) => c.email).length})`}
              </Button>
            </span>
          </Tooltip> */}
        </Box>

        {/* DataGrid */}
        <Paper sx={{ height: 600, width: "100%" }}>
          <DataGrid
            rows={clientes}
            columns={columns}
            localeText={ptBR.components.MuiDataGrid.defaultProps.localeText}
            disableRowSelectionOnClick
            // ── Seleção — sintaxe MUI 5.x ──────────────────────────────────
            checkboxSelection
            selectionModel={selectionModel}
            onSelectionModelChange={(newSelection) => {
              setSelectionModel(newSelection);
            }}
            // ───────────────────────────────────────────────────────────────

            slots={{
              toolbar: GridToolbar,
              loadingOverlay: CircularProgress,
            }}
            slotProps={{
              toolbar: {
                showQuickFilter: true,
                quickFilterProps: { debounceMs: 500 },
              },
            }}
            initialState={{
              pagination: { paginationModel: { pageSize: 25 } },
              sorting: {
                sortModel: [{ field: "diasInativo", sort: "desc" }],
              },
            }}
            pageSizeOptions={[10, 25, 50, 100]}
            density="standard"
          />
        </Paper>
      </Box>
    </Container>
  );
}

export default RelatorioInatividade;
